// src/components/Chat/MediaMessage.jsx
import React, { useState, useEffect } from 'react';
import { MEDIA_TYPES } from '../../../store/services/chatService';

const MediaMessage = ({ message, isCurrentUser }) => {
  const [imageError, setImageError] = useState(false);
  const [videoError, setVideoError] = useState(false);

  if (!message.media_type || message.media_type === MEDIA_TYPES.NONE) {
    return null;
  }

  const getMediaContent = () => {
    switch (message.media_type) {
      case MEDIA_TYPES.IMAGE:
        return (
          <div className="media-image-container">
            {!imageError ? (
              <img
                src={message.media_path}
                alt="Shared image"
                className="img-fluid rounded"
                style={{ maxWidth: '300px', maxHeight: '300px', cursor: 'pointer' }}
                onClick={() => window.open(message.media_path, '_blank')}
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="media-placeholder bg-light rounded d-flex align-items-center justify-content-center"
                   style={{ width: '300px', height: '200px' }}>
                <i className="fas fa-image fa-2x text-muted"></i>
              </div>
            )}
          </div>
        );

      case MEDIA_TYPES.VIDEO:
        return (
          <div className="media-video-container">
            {!videoError ? (
              <video
                controls
                className="rounded"
                style={{ maxWidth: '300px', maxHeight: '300px' }}
                poster={message.media_data?.thumbnail}
                onError={() => setVideoError(true)}
              >
                <source src={message.media_path} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            ) : (
              <div className="media-placeholder bg-light rounded d-flex align-items-center justify-content-center"
                   style={{ width: '300px', height: '200px' }}>
                <i className="fas fa-video fa-2x text-muted"></i>
                <span className="ms-2">Video unavailable</span>
              </div>
            )}
            {message.media_duration && (
              <small className="text-muted d-block mt-1">
                Duration: {formatDuration(message.media_duration)}
              </small>
            )}
          </div>
        );

      case MEDIA_TYPES.AUDIO:
        return (
          <div className="media-audio-container">
            <audio controls className="w-105">
              <source src={message.media_path} type="audio/mpeg" />
              Your browser does not support the audio element.
            </audio>
            {message.media_duration && (
              <small className="text-muted d-block mt-1">
                Duration: {formatDuration(message.media_duration)}
              </small>
            )}
          </div>
        );

      case MEDIA_TYPES.LOCATION:
        const location = message.media_data || {};
        return (
          <div className="media-location-container border rounded p-3 bg-light">
            <div className="d-flex align-items-center">
              <i className="fas fa-map-marker-alt fa-2x text-danger me-3"></i>
              <div>
                <h6 className="mb-1">Location Shared</h6>
                <small className="text-muted">
                  {location.address || `Lat: ${location.lat}, Lng: ${location.lng}`}
                </small>
                <br />
                <a
                  href={`https://maps.google.com/?q=${location.lat},${location.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-outline-primary mt-2"
                >
                  View on Map
                </a>
              </div>
            </div>
          </div>
        );

      case MEDIA_TYPES.SEND_PROFILE:
        const profile = message.media_data || {};
        return (
          <div className="media-profile-container border rounded p-3 bg-light">
            <div className="d-flex align-items-center">
              {profile.image ? (
                <img
                  src={profile.image}
                  alt={profile.name}
                  className="rounded-circle me-3"
                  style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                />
              ) : (
                <div className="rounded-circle bg-secondary me-3 d-flex align-items-center justify-content-center"
                     style={{ width: '50px', height: '50px' }}>
                  <i className="fas fa-user text-white"></i>
                </div>
              )}
              <div>
                <h6 className="mb-1">Profile Shared</h6>
                <p className="mb-1 fw-bold">{profile.name}</p>
                <small className="text-muted">{profile.type}</small>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="media-other-container border rounded p-3 bg-light">
            <i className="fas fa-paperclip me-2"></i>
            <span>{message.media_type.replace('_', ' ')} shared</span>
          </div>
        );
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`media-message ${isCurrentUser ? 'sent' : 'received'}`}>
      {getMediaContent()}
      {message.message && (
        <div className="mt-2">
          <p className="mb-0">{message.message}</p>
        </div>
      )}
    </div>
  );
};

export default MediaMessage;